import { useState } from "react";
import { useNavigate } from "react-router";
import { Box, Typography, Button, Paper, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { URLHandler } from "src/services/URLHandler";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { User } from "src/api/client-api/user/useAllUsers";
import { UserListContextProvider, useUserListContext } from "./UserListContext";
import { UserGrid } from "./UserGrid";
import { UserModal } from "./UserModal";
import { GridTableActions } from "src/components/Grid/headers/GridTableActions";
import { useColumnsTableAction } from "src/components/Grid/headers/useColumnsTableAction";
import { resolveUserManagementPermissions } from "src/pages/Settings/Users/userManagementPermissions";
import { UserOrganizationRolesModal } from "src/pages/Settings/Users/UserOrganizationRolesModal";

type UsersListProps = Record<string, never>;

const UsersListContent = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { canUser, isAdmin } = useFoodpilotContext();

    const { searchUser, setSearchUser, showCreateModal, setShowCreateModal, mrt_columns } = useUserListContext();
    const columnsAction = useColumnsTableAction(mrt_columns);
    const { canCreateUsers, canModifyUsers, canAssignUserRoles, canManageUsers } = resolveUserManagementPermissions(
        canUser,
        isAdmin,
    );

    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [roleAssignmentUser, setRoleAssignmentUser] = useState<User | null>(null);

    const handleCreateUser = () => {
        setEditingUser(null);
        setShowCreateModal(true);
    };

    const handleModalSuccess = () => {
        setShowCreateModal(false);
        setEditingUser(null);
    };

    const handleCloseModal = () => {
        setShowCreateModal(false);
        setEditingUser(null);
    };

    const handleCreatedForEdit = (createdUser: User) => {
        if (canModifyUsers) {
            setEditingUser(createdUser);
            return;
        }

        setShowCreateModal(false);
        setRoleAssignmentUser(createdUser);
    };

    if (!canManageUsers) {
        return <div>{t("common.accessDenied", "Access denied")}</div>;
    }

    return (
        <Box sx={{ padding: "32px" }}>
            <Paper
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box
                        sx={{
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                        onClick={() => navigate(URLHandler.settings.myOrganization())}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                    </Box>
                    <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                        {t("users.title", "Users")}
                    </Typography>
                </Box>

                {/* Search and Add User Section */}
                <Box
                    sx={{
                        padding: "24px 32px",
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", md: "center" },
                        gap: "24px",
                    }}
                >
                    {/* Search */}
                    <TextField
                        placeholder={t("users.search", "Search users...")}
                        value={searchUser}
                        onChange={(e) => setSearchUser(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "#A3A2A0", fontSize: "1.25rem" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            maxWidth: { xs: "none", md: "323px" },
                            flex: { md: 1 },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "24px",
                                minHeight: "40px",
                                backgroundColor: "white",
                                "& fieldset": {
                                    borderColor: "#E7E6E4",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#D0D0D0",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: (theme) => theme.palette.primary[600],
                                },
                            },
                            "& .MuiInputBase-input": {
                                fontSize: "0.875rem",
                                padding: "0px 16px !important",
                                color: "#393A36",
                                "&::placeholder": {
                                    color: "#A3A2A0",
                                    opacity: 1,
                                },
                            },
                        }}
                    />

                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
                        <GridTableActions actions={[columnsAction]} appearance="legacy" />

                        {canCreateUsers && (
                            <Button
                                variant="contained"
                                startIcon={<FoodsIcon icon="plus" size={1} />}
                                onClick={handleCreateUser}
                                sx={(theme) => ({
                                    borderRadius: "44px",
                                    padding: "0px 20px",
                                    height: "40px",
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    textTransform: "none",
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    color: "white !important",
                                    boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                    "&:hover": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                        boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                        color: "white !important",
                                    },
                                    "&:disabled": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                        opacity: 0.36,
                                        color: "white !important",
                                    },
                                })}
                            >
                                {t("users.addUser", "Add User")}
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Users Grid */}
                <Box sx={{ padding: "0px 32px 32px" }}>
                    <UserGrid />
                </Box>
            </Paper>

            {/* Create User Modal */}
            {canCreateUsers && (
                <UserModal
                    open={showCreateModal}
                    onClose={handleCloseModal}
                    onSuccess={handleModalSuccess}
                    onCreatedForEdit={handleCreatedForEdit}
                    editingUser={editingUser ?? undefined}
                />
            )}
            {canAssignUserRoles && (
                <UserOrganizationRolesModal
                    open={roleAssignmentUser !== null}
                    onClose={() => setRoleAssignmentUser(null)}
                    user={roleAssignmentUser}
                />
            )}
        </Box>
    );
};

export const UsersList = (props: UsersListProps) => {
    return (
        <UserListContextProvider>
            <UsersListContent />
        </UserListContextProvider>
    );
};
